import React, { useEffect, useMemo, useState } from 'react';
import Konva from 'konva';
import { Stage, Layer, Group, Text, Line, Rect, Image as KonvaImage } from 'react-konva';
import useImage from 'use-image';
import { projectFastenerDrawing } from '../utils/igesExporter';
import { layoutFastenerSheet, FastenerSheetGraphics } from './FastenerDrawingDialog';
import { fastenerDisplayName } from './fastenersData';

export const SHEET_W = 793;
export const SHEET_H = 1122;
const SHEET_GAP = 36;
const WORK = { x: 36, y: 40, width: 721, height: 820 };

const STAMP_TITLES = {
  'YKRS-01': 'Крепеж\nдля коллекторов серии\nGSK/GK из нержавеющей\nстали',
  'YKRS-02': 'Крепеж усиленный\nдля коллекторов серии\nGSK/GK из нержавеющей\nстали',
  'YKWK-01': 'Крепеж\nдля коллекторов серии\nWK из нержавеющей\nстали',
  'YKWK-02': 'Крепеж усиленный\nдля коллекторов серии\nWK из нержавеющей\nстали',
  'KRNS-GS-03': 'Крепеж напольный\nрегулируемый\nиз нержавеющей\nстали',
  'KRNS-GS-04': 'Крепеж напольный\nрегулируемый\nиз нержавеющей\nстали',
  'Крепеж напольный нерегулируемый': 'Крепеж напольный\nнерегулируемый\nиз нержавеющей\nстали',
};

function readRequest() {
  const params = new URLSearchParams(window.location.search);
  let sizes = [];
  try {
    const raw = params.get('sizes');
    if (raw) sizes = JSON.parse(raw);
  } catch (err) {
    sizes = [];
  }
  if (!Array.isArray(sizes) || sizes.length === 0) {
    sizes = [{
      d: params.get('d') ?? '',
      a: params.get('a') ?? '',
      v: params.get('v') ?? '',
      h: params.get('h') ?? '',
    }];
  }
  return {
    key: params.get('key') || '',
    sizes: sizes.map((size) => ({
      clampDiameter: Number(size.d) || undefined,
      axialDistance: Number(size.a) || undefined,
      overhang: Number(size.v) || undefined,
      height: Number(size.h) || undefined,
      rangeOffset: Number(size.o) || 0,
    })),
  };
}

function workshopHeaderText(workshopInfo) {
  let deadline = workshopInfo?.deadline || '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(deadline)) {
    const [year, month, day] = deadline.split('-');
    deadline = `${day}.${month}.${year}`;
  }
  return `Сделка: ${workshopInfo?.deal || ''}. Крайний срок: ${deadline}. г.${workshopInfo?.city || ''}`;
}

function ProductTitle({ text }) {
  const raw = String(text || '').trim();
  if (!raw) return null;
  const value = /^крепежи для\b/i.test(raw) ? raw : `Крепежи для ${raw}`;
  const maxWidth = 500;
  let fontSize = 22;
  let measured = new Konva.Text({
    text: value,
    fontSize,
    fontFamily: 'Arial',
    fontStyle: 'italic',
  });
  while (fontSize > 11 && measured.width() > maxWidth) {
    fontSize -= 1;
    measured = new Konva.Text({
      text: value,
      fontSize,
      fontFamily: 'Arial',
      fontStyle: 'italic',
    });
  }
  return (
    <Text
      x={64}
      y={937}
      offsetY={measured.height() / 2}
      text={value}
      fontSize={fontSize}
      fontFamily="Arial"
      fontStyle="italic"
      fill="#000000"
      listening={false}
    />
  );
}

function WorkshopMarks({ workshopInfo, count = 1, weldRub = 0 }) {
  const text = workshopHeaderText(workshopInfo);
  const baseFontSize = 20;
  const availableWidth = 560;
  const estimated = text.length * baseFontSize * 0.5;
  const fontSize = estimated > availableWidth
    ? Math.max(10, Math.floor(baseFontSize * (availableWidth / estimated)))
    : baseFontSize;
  const orderQty = Math.max(1, parseInt(workshopInfo?.quantity, 10) || 1);
  const pieces = Math.max(1, count) * orderQty;
  const weldNumber = Number(weldRub);
  const rows = [
    { label: 'Количество', value: `${pieces} шт.` },
  ];
  if (Number.isFinite(weldNumber) && weldNumber > 0) {
    rows.push({ label: 'Сварка', value: `${Math.round(weldNumber).toLocaleString('ru-RU')} ₽` });
  }
  const x = 589;
  const y = 912;
  const colWidths = [110, 80];
  const rowHeight = 25;
  const tableWidth = colWidths[0] + colWidths[1];
  const tableHeight = rowHeight * rows.length;

  return (
    <Group listening={false}>
      <Text
        x={80}
        y={30}
        text={text}
        fontSize={fontSize}
        fontFamily="Arial"
        fontStyle="italic"
        fill="#000000"
        listening={false}
      />
      <Line points={[52, 55, 742, 55]} stroke="#000000" strokeWidth={2} listening={false} />
      <Group x={x} y={y}>
        <Rect
          width={tableWidth}
          height={tableHeight}
          fill="#ffffff"
          stroke="#000000"
          strokeWidth={2}
          listening={false}
        />
        <Line
          points={[colWidths[0], 0, colWidths[0], tableHeight]}
          stroke="#000000"
          strokeWidth={1}
          listening={false}
        />
        {rows.map((row, index) => (
          <Group key={row.label}>
            {index < rows.length - 1 && (
              <Line
                points={[0, rowHeight * (index + 1), tableWidth, rowHeight * (index + 1)]}
                stroke="#cccccc"
                strokeWidth={0.5}
                listening={false}
              />
            )}
            <Text
              x={8}
              y={rowHeight * index + 5}
              width={colWidths[0] - 12}
              text={row.label}
              fontSize={12}
              fontFamily="Arial"
              fill="#000000"
              listening={false}
            />
            <Text
              x={colWidths[0] + 8}
              y={rowHeight * index + 5}
              width={colWidths[1] - 12}
              text={row.value}
              fontSize={12}
              fontFamily="Arial"
              fill="#000000"
              listening={false}
            />
          </Group>
        ))}
      </Group>
    </Group>
  );
}

function formatMass(kg) {
  if (!Number.isFinite(kg) || kg <= 0) return '';
  return kg >= 10 ? kg.toFixed(1) : kg.toFixed(2);
}

export function SheetPage({ partKey, size, payload, error, background, workshopInfo, productTitle }) {
  const sheet = payload ? layoutFastenerSheet(payload) : null;
  const stampName = fastenerDisplayName({
    itemKey: partKey,
    clampDiameter: size?.clampDiameter,
    axialDistance: size?.axialDistance,
    overhang: size?.overhang,
    fastenerHeight: size?.height,
    rangeOffset: size?.rangeOffset,
  });
  const nameFont = stampName.length > 12 ? 16 : 22;
  const massValue = formatMass(payload?.massKg);
  const massText = massValue ? `${massValue}\nкг` : '';
  const margin = 18;
  const fit = sheet
    ? Math.min(
      (WORK.width - margin * 2) / sheet.width,
      (WORK.height - margin * 2) / sheet.height,
    ) * (sheet.maxFit || 1)
    : 1;
  // Внутренняя рамка А3 шире слева: её середина правее середины листа.
  const frameCenterX = 416;
  const placeX = sheet
    ? (Number.isFinite(sheet.anchorX)
      ? frameCenterX - sheet.anchorX * fit
      : WORK.x + margin + (WORK.width - margin * 2 - sheet.width * fit) / 2 - sheet.minX * fit)
    : WORK.x;
  const placeY = sheet
    ? WORK.y + margin + (WORK.height - margin * 2 - sheet.height * fit) / 2 - sheet.minY * fit
    : WORK.y;

  return (
    <Group>
      <Rect
        x={0}
        y={0}
        width={SHEET_W}
        height={SHEET_H}
        fill="#ffffff"
        listening={false}
      />
      {background && (
        <KonvaImage image={background} width={SHEET_W} height={SHEET_H} listening={false} />
      )}
      {error && (
        <Text
          x={WORK.x}
          y={WORK.y + 80}
          width={WORK.width}
          text={error}
          fontSize={16}
          fontFamily="Arial"
          fill="#b00020"
          listening={false}
        />
      )}
      {!sheet && !error && (
        <Text
          x={WORK.x}
          y={WORK.y + 80}
          width={WORK.width}
          align="center"
          text="Строю чертёж по модели…"
          fontSize={16}
          fontFamily="Arial"
          fill="#333"
          listening={false}
        />
      )}
      {sheet && (
        <Group x={placeX} y={placeY} scaleX={fit} scaleY={fit}>
          <FastenerSheetGraphics sheet={sheet} />
        </Group>
      )}
      {workshopInfo && (
        <WorkshopMarks
          workshopInfo={workshopInfo}
          count={size?.count}
          weldRub={size?.weldRub}
        />
      )}
      {productTitle && <ProductTitle text={productTitle} />}
      {partKey && (
        <>
          <Text
            x={460}
            y={963}
            width={320}
            height={38}
            align="center"
            verticalAlign="middle"
            text={stampName}
            fontSize={nameFont}
            fontFamily="Arial"
            fontStyle="italic"
            fill="#000000"
            listening={false}
          />
          <Text
            x={464}
            y={1004}
            width={178}
            height={62}
            align="center"
            verticalAlign="middle"
            text={STAMP_TITLES[partKey] || partKey}
            fontSize={13}
            fontFamily="Arial"
            fontStyle="italic"
            lineHeight={1.05}
            fill="#000000"
            listening={false}
          />
          {massText && (
            <Text
              x={686}
              y={1016}
              width={46}
              height={38}
              align="center"
              verticalAlign="middle"
              text={massText}
              fontSize={13}
              fontFamily="Arial"
              fontStyle="italic"
              lineHeight={0.9}
              fill="#000000"
              listening={false}
            />
          )}
          <Text
            x={460}
            y={1079}
            width={186}
            align="center"
            text="AISI201(1.5мм)"
            fontSize={15}
            fontFamily="Arial"
            fontStyle="italic"
            fill="#000000"
            listening={false}
          />
        </>
      )}
    </Group>
  );
}

export default function FastenerSheetWindow() {
  const request = useMemo(readRequest, []);
  const [background] = useImage(`${process.env.PUBLIC_URL || ''}/A3React(POV).png`);
  const [pages, setPages] = useState(() => request.sizes.map(() => ({ payload: null, error: '' })));
  const [viewport, setViewport] = useState({
    w: window.innerWidth,
    h: window.innerHeight,
  });

  useEffect(() => {
    const size = request.sizes[0];
    const name = request.sizes.length === 1
      ? fastenerDisplayName({
        itemKey: request.key,
        clampDiameter: size?.clampDiameter,
        axialDistance: size?.axialDistance,
        overhang: size?.overhang,
        fastenerHeight: size?.height,
        rangeOffset: size?.rangeOffset,
      })
      : request.key;
    document.title = name ? `Чертёж ${name}` : 'Чертёж крепежа';
  }, [request]);

  useEffect(() => {
    const onResize = () => setViewport({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!request.key) {
      setPages([{ payload: null, error: 'Не указан крепеж' }]);
      return undefined;
    }
    let cancelled = false;
    Promise.all(request.sizes.map((size) => projectFastenerDrawing({
      partKey: request.key,
      clampDiameter: size.clampDiameter,
      axialDistance: size.axialDistance,
      overhang: size.overhang,
      height: size.height,
      rangeOffset: size.rangeOffset,
    }).then((payload) => ({ payload, error: '' })).catch((err) => ({
      payload: null,
      error: err?.message || 'Не удалось построить чертёж',
    })))).then((data) => {
      if (!cancelled) setPages(data);
    });
    return () => { cancelled = true; };
  }, [request]);

  const count = Math.max(pages.length, 1);
  const rowW = count * SHEET_W + (count - 1) * SHEET_GAP;
  const viewScale = Math.max(0.15, Math.min(viewport.w / rowW, viewport.h / SHEET_H));

  return (
    <div style={{
      width: '100vw',
      height: '100vh',
      overflow: 'hidden',
      background: '#fafafa',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
    >
      <Stage width={rowW * viewScale} height={SHEET_H * viewScale} scaleX={viewScale} scaleY={viewScale}>
        <Layer>
          {pages.map((page, index) => (
            <Group key={index} x={index * (SHEET_W + SHEET_GAP)}>
              <SheetPage
                partKey={request.key}
                size={request.sizes[index]}
                payload={page.payload}
                error={page.error}
                background={background}
              />
            </Group>
          ))}
        </Layer>
      </Stage>
    </div>
  );
}
